import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../services/AuthContext';
import * as api from '../services/api';
import './FinalQuiz.css'; // Reuse existing quiz styles
import { Icon, FeatureIcon, Spinner, ButtonPrimary } from '../components/ComponentLibrary';

export default function TakeQuiz() {
    const { quizId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();

    const [quiz, setQuiz] = useState(null);
    const [questions, setQuestions] = useState([]);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [answers, setAnswers] = useState({});
    const [timeLeft, setTimeLeft] = useState(0);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [result, setResult] = useState(null);

    // 1. Load Quiz Data & Check Access
    useEffect(() => {
        const fetchQuizData = async () => {
            try {
                setLoading(true);

                // Parallel fetch: Quiz Data + Check Permission
                const [quizData, attemptStatus] = await Promise.all([
                    api.getQuizById(quizId),
                    api.checkCanAttempt(quizId)
                ]);

                // 1. Security Check (Track)
                if (user && user.role === 'student' && quizData.track !== user.track) {
                    setError('🚫 هذا الامتحان غير متاح لمسارك الحالي.');
                    return;
                }

                // 2. Check Attempts
                if (!attemptStatus.canAttempt) {
                    setError(`⛔ لقد استنفذت جميع المحاولات المسموحة لهذا الامتحان (${quizData.maxAttempts} محاولات).`);
                    return;
                }

                setQuiz(quizData);
                setQuestions(quizData.questions || []);
                setTimeLeft(quizData.timeLimit * 60);
            } catch (err) {
                console.error('Fetch quiz error:', err);
                setError(err.response?.data?.message || 'فشل تحميل الامتحان');
            } finally {
                setLoading(false);
            }
        };

        if (quizId && user) {
            fetchQuizData();
        }
    }, [quizId, user]);

    // 2. Timer Logic
    useEffect(() => {
        if (timeLeft <= 0 || result || loading || error) return; // Don't run timer if error

        const timer = setInterval(() => {
            setTimeLeft(prev => {
                if (prev <= 1) {
                    clearInterval(timer);
                    handleSubmit();
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [timeLeft, result, loading, error]);

    // 3. Handlers
    const handleOptionSelect = (questionId, optionKey) => {
        setAnswers(prev => ({
            ...prev,
            [questionId]: optionKey
        }));
    };

    const handleNext = () => {
        if (currentQuestionIndex < questions.length - 1) {
            setCurrentQuestionIndex(prev => prev + 1);
        } else {
            handleSubmit();
        }
    };

    const handleSubmit = async () => {
        if (submitting || result) return;

        try {
            setSubmitting(true);
            const timeTaken = (quiz.timeLimit * 60) - timeLeft;
            const responseData = await api.submitQuizAttempt(quizId, answers, timeTaken);
            setResult(responseData.data || responseData);
        } catch (err) {
            console.error('Submit error details:', err.response?.data || err.message);

            // If max attempts reached (403) or bad request (400), reload immediately without alert
            if (err.response && (err.response.status === 403 || err.response.status === 400)) {
                window.location.reload();
                return;
            }

            const msg = err.response?.data?.message || err.message || 'فشل إرسال الإجابات. تأكد من اتصالك بالإنترنت وحاول مجدداً.';
            alert(`تنبيه: ${msg}`);
        } finally {
            setSubmitting(false);
        }
    };


    // --- Render Logic ---

    if (loading) return <div className="loading-container"><Spinner size="lg" /><p>جاري تحضير الامتحان...</p></div>;

    if (error) {
        return (
            <div className="error-container">
                <div className="error-card">
                    <h1><Icon name="target" size={40} /> تنبيه</h1>
                    <p>{error}</p>
                    <ButtonPrimary onClick={() => navigate('/final-quiz')}>
                        العودة لقائمة الامتحانات
                    </ButtonPrimary>
                </div>
            </div>
        );
    }

    if (result) {
        const { score, totalPoints, percentage, passed, results = [] } = result; // Safe default

        return (
            <div className="final-quiz-container">
                <div className="result-card">
                    <FeatureIcon name={passed ? 'achievement' : 'progress'} variant={passed ? 'sunshine' : 'ocean'} size="xl" className="mb-6" />
                    <h2>{passed ? <>مبروك! لقد نجحت <Icon name="sparkles" /></> : 'حاول مرة أخرى!'}</h2>

                    <div className="score-display">
                        {score} <span>/ {totalPoints}</span>
                    </div>
                    <div className="percentage-bar">
                        <div className="bar-fill" style={{ width: `${percentage}%`, background: passed ? '#2ecc71' : '#e74c3c' }}></div>
                    </div>

                    <p className={`status-text ${passed ? 'passed' : 'failed'}`}>
                        النسبة المئوية: {percentage}%
                    </p>

                    <div className="action-buttons">
                        <button className="next-btn" onClick={() => navigate('/final-quiz')}>
                            العودة للرئيسية
                        </button>
                        {!passed && (
                            <button className="next-btn retry" onClick={() => window.location.reload()}>
                                إعادة المحاولة <Icon name="clock" size={16} />
                            </button>
                        )}
                    </div>
                </div>

                {/* --- Review Section --- */}
                {/* --- Review Section --- */}
                {results && results.length > 0 && (
                    <div className="review-section">
                        <h3 className="review-title">مراجعة الإجابات <Icon name="clipboard" size={24} /></h3>
                        <div className="review-grid">
                            {questions.map((q, index) => {
                                // Find the result matching this question
                                // Results array from backend uses questionId (ObjectId)
                                // Safe find with robust ID matching
                                const qResult = results.find(r =>
                                    r.questionId === q._id ||
                                    r.questionId === q.id ||
                                    (r.questionId && q._id && r.questionId.toString() === q._id.toString())
                                );

                                if (!qResult) return null;

                                return (
                                    <div key={q._id} className={`review-card ${qResult.isCorrect ? 'correct' : 'wrong'}`}>
                                        <div className="review-header">
                                            <span className="q-num">سؤال {index + 1}</span>
                                            <span className="q-status">
                                                {qResult.isCorrect ? <span className="flex items-center gap-1 text-green-600"><Icon name="check" size={16} /> إجابة صحيحة</span> : <span className="flex items-center gap-1 text-red-500"><Icon name="x" size={16} /> إجابة خاطئة</span>}
                                            </span>
                                        </div>
                                        <h4 className="review-question text-lg font-bold mb-4">{q.questionText}</h4>

                                        <div className="review-options">
                                            {Object.entries(q.options)
                                                .filter(([key]) => !['id', '_id', '__v'].includes(key) && q.options[key])
                                                .map(([key, val]) => {
                                                    const isUserSelect = qResult.userAnswer === key;
                                                    const isCorrect = qResult.correctAnswer === key;

                                                    let optionClass = 'review-option';
                                                    if (isCorrect) optionClass += ' correct-answer';
                                                    else if (isUserSelect && !isCorrect) optionClass += ' wrong-answer';

                                                    return (
                                                        <div key={key} className={optionClass}>
                                                            <span className="opt-key">{key}</span>
                                                            <span className="opt-val">{val}</span>
                                                            {isCorrect && <Icon name="check" size={18} className="text-green-700 mr-auto" />}
                                                            {isUserSelect && !isCorrect && <Icon name="x" size={18} className="text-red-600 mr-auto" />}
                                                        </div>
                                                    );
                                                })}
                                        </div>

                                        {!qResult.isCorrect && (
                                            <div className="correction-note mt-3 p-2 bg-red-50 text-red-700 rounded-md border border-red-100 text-sm">
                                                💡 الإجابة الصحيحة هي: <strong>{qResult.correctAnswer}</strong>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                <style>{`
                    .review-section { max-width: 800px; margin: 40px auto; width: 100%; animation: slideUp 0.6s ease-out; }
                    .review-title { text-align: center; font-size: 1.8rem; color: #1e293b; margin-bottom: 30px; font-weight: 800; display: flex; align-items: center; justify-content: center; gap: 10px; }
                    .review-grid { display: flex; flex-direction: column; gap: 24px; }
                    
                    .review-card { background: white; border-radius: 16px; padding: 24px; border: 2px solid #e2e8f0; position: relative; overflow: hidden; transition: transform 0.2s; }
                    .review-card:hover { transform: translateY(-2px); box-shadow: 0 10px 20px rgba(0,0,0,0.05); }
                    
                    .review-card.correct { border-color: #86efac; background: linear-gradient(to bottom, #f0fdf4, #fff); }
                    .review-card.wrong { border-color: #fca5a5; background: linear-gradient(to bottom, #fef2f2, #fff); }

                    .review-header { display: flex; justify-content: space-between; margin-bottom: 15px; font-size: 0.95rem; font-weight: 700; color: #64748b; border-bottom: 1px solid rgba(0,0,0,0.05); padding-bottom: 10px; }
                    
                    .review-options { display: grid; gap: 12px; }
                    .review-option { 
                        display: flex; align-items: center; padding: 14px 18px; 
                        background: white; border: 2px solid #e2e8f0; border-radius: 10px; 
                        font-size: 1rem; color: #475569; transition: all 0.2s;
                    }

                    .review-option.correct-answer { border-color: #22c55e; background-color: #dcfce7; color: #14532d; font-weight: 700; box-shadow: 0 2px 5px rgba(34, 197, 94, 0.1); }
                    .review-option.wrong-answer { border-color: #ef4444; background-color: #fee2e2; color: #7f1d1d; opacity: 1; text-decoration: line-through; text-decoration-color: #ef4444; }

                    .opt-key { font-weight: 800; margin-left: 12px; min-width: 28px; height: 28px; display: flex; align-items: center; justify-content: center; background: #f1f5f9; border-radius: 6px; font-size: 0.9rem; }
                    .review-option.correct-answer .opt-key { background: #bbf7d0; color: #14532d; }
                    .review-option.wrong-answer .opt-key { background: #fecaca; color: #7f1d1d; }
                    
                    .mr-auto { margin-right: auto; }

                    @keyframes slideUp { from { opacity: 0; transform: translateY(40px); } to { opacity: 1; transform: translateY(0); } }
                `}</style>

            </div>
        );
    }

    const currentQuestion = questions[currentQuestionIndex];
    const progressPercent = ((currentQuestionIndex + 1) / questions.length) * 100;

    return (
        <div className="final-quiz-container">
            <div className="quiz-interface">
                <div className="quiz-header-bar">
                    <div className="timer">
                        <Icon name="clock" size={18} /> {Math.floor(timeLeft / 60)}:{(timeLeft % 60).toString().padStart(2, '0')}
                    </div>
                    <div className="progress-track">
                        <div className="progress-fill" style={{ width: `${progressPercent}%` }}></div>
                    </div>
                    <div className="q-counter">
                        {currentQuestionIndex + 1} / {questions.length}
                    </div>
                </div>

                <div className="question-area">
                    <h2 className="question-text">{currentQuestion?.questionText}</h2>
                </div>

                <div className="options-grid">
                    {currentQuestion && Object.entries(currentQuestion.options)
                        .filter(([key, value]) => {
                            // Exclude MongoDB internal fields and ID
                            if (['id', '_id', '__v'].includes(key)) return false;
                            return value && value.trim() !== '';
                        })
                        .map(([key, value]) => (
                            <button
                                key={key}
                                className={`option-btn ${answers[currentQuestion._id] === key ? 'selected' : ''}`}
                                onClick={() => handleOptionSelect(currentQuestion._id, key)}
                            >
                                <span className="option-key">{key}</span>
                                <span className="option-value">{value}</span>
                            </button>
                        ))}
                </div>

                <div className="quiz-footer">
                    <button className="exit-btn" onClick={() => navigate('/final-quiz')}>خروج</button>
                    <button
                        className={`next-btn ${submitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                        onClick={handleNext}
                        disabled={!answers[currentQuestion?._id] || submitting}
                    >
                        {submitting ? (
                            <><Spinner size="sm" className="ml-2" /> جاري الإرسال...</>
                        ) : (
                            currentQuestionIndex === questions.length - 1 ?
                                <>إنهاء الامتحان <Icon name="check" size={18} /></> :
                                <>السؤال التالي <Icon name="next" size={18} /></>
                        )}
                    </button>
                </div>
            </div>

            <style>{`
                .loading-container { display: flex; flex-direction: column; align-items: center; justify-content: center; height: 80vh; }
                .error-container { display: flex; justify-content: center; align-items: center; height: 70vh; padding: 20px; }
                .error-card { background: white; padding: 40px; border-radius: 20px; text-align: center; box-shadow: 0 10px 30px rgba(0,0,0,0.1); max-width: 500px; }
                .error-card h1 { color: #e74c3c; margin-bottom: 20px; }
                
                .q-counter { font-weight: bold; color: #003049; }
                .option-key { font-weight: 800; margin-right: 10px; background: rgba(0,0,0,0.05); padding: 4px 8px; border-radius: 4px; }
                
                .percentage-bar { width: 100%; height: 10px; background: #eee; border-radius: 5px; margin: 20px 0; overflow: hidden; }
                .bar-fill { height: 100%; transition: width 0.5s ease; }
                
                .status-text { font-size: 1.2rem; font-weight: 700; margin-bottom: 30px; }
                .passed { color: #2ecc71; }
                .failed { color: #e74c3c; }
                
                .action-buttons { display: flex; gap: 15px; justify-content: center; }
                .retry { background: #f1faee; color: #1d3557; border: 2px solid #1d3557; }
                
                .exit-btn { background: none; border: none; color: #888; cursor: pointer; font-weight: 600; }
            `}</style>
        </div>
    );
}
